import { appraisifyPDFTheme } from '@aloft/toolkit-sui';
import { Font, StyleSheet, Styles } from '@react-pdf/renderer';

const { colors, fontSizes } = appraisifyPDFTheme;

// Never split a word
Font.registerHyphenationCallback((word) => [word]);

const headerText: Styles[string] = {
  color: colors.text,
  fontFamily: 'Lato-Bold',
  fontWeight: 'bold',
  lineHeight: 'normal',
};

const body: Styles[string] = {
  color: colors.text,
  fontFamily: 'Lato-Regular',
  fontSize: fontSizes.md,
  fontWeight: 'normal',
  lineHeight: '150%',
};

export const fontStyles = StyleSheet.create({
  h1: {
    ...headerText,
    color: colors.background,
    fontSize: fontSizes.xxxl,
  },
  h3: {
    ...headerText,
    fontSize: fontSizes.xl,
  },
  h4: {
    ...headerText,
    fontSize: fontSizes.lg,
  },
  h4Light: {
    ...headerText,
    fontSize: fontSizes.lg,
    color: colors.textLight,
  },
  h5: {
    ...headerText,
    fontSize: fontSizes.md,
  },
  body,
  bodyLight: {
    ...body,
    color: colors.textLight,
    fontFamily: 'Lato-Light',
  },
  bodyLarge: {
    ...body,
    fontSize: fontSizes.lg,
  },
  small: {
    ...body,
    fontFamily: 'Lato-Light',
    fontSize: fontSizes.sm,
  },
  bodyBold: {
    ...body,
    fontFamily: 'Lato-Bold',
    fontWeight: 700,
  },
  listItem: {
    ...body,
    width: 20,
    textAlign: 'right',
    paddingRight: 4,
  },
});

export const pageStyles = StyleSheet.create({
  page: {
    display: 'flex',
    paddingHorizontal: 24,
    paddingVertical: 16,
    flexDirection: 'column',
    gap: 8,
  },
});

export const coverPageStyles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: colors.backgroundAccent,
    color: colors.background,
    height: '100%',
  },
  header: {
    padding: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  headerRow: { flexDirection: 'row', alignItems: 'flex-end' },
  headerLogo: { width: 150, height: 40 },
  headerProductTypeTag: {
    fontSize: fontSizes.lg,
    color: colors.background,
    marginLeft: 10,
    backgroundColor: colors.backgroundAccentLight,
    padding: 4,
    borderRadius: 4,
  },
  propertyImage: { width: 375, height: 250, objectFit: 'cover' },
  details: {
    padding: 24,
    backgroundColor: colors.background,
    color: colors.text,
    flex: 1,
    justifyContent: 'space-between',
  },
  detail: { textTransform: 'uppercase', marginBottom: 4 },
  tags: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: 12,
    color: colors.background,
    fontSize: fontSizes.sm,
  },
  tag: {
    marginLeft: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  tagValue: {
    fontWeight: 'bold',
    backgroundColor: colors.backgroundAccentLight,
    padding: 4,
    borderRadius: 4,
  },
});

export const headerStyles = StyleSheet.create({
  header: {
    display: 'flex',
    flexDirection: 'row',
    paddingVertical: 8,
    paddingHorizontal: 0,
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
    borderBottomWidth: 1,
    borderBottomColor: appraisifyPDFTheme.colors.border,
  },
  logoWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
  },
  logo: {
    width: 90,
    height: 30,
    objectFit: 'contain',
    paddingBottom: 8,
  },
});

export const propertyOrderDetailsPageStyles = StyleSheet.create({
  page: {
    padding: 32,
  },
  propertyImage: { width: 250, height: 250, objectFit: 'cover' },
  section: {
    flexDirection: 'row',
    gap: 16,
    paddingVertical: 16,
  },
  details: {
    flexDirection: 'column',
    flex: 1,
    gap: 8,
  },
  detail: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  label: {
    width: 100,
    flexShrink: 0,
    paddingRight: 8,
  },
  value: {
    flex: 1,
    minWidth: 0,
  },
});

export const salesComparisonPageStyles = StyleSheet.create({
  content: {
    marginTop: 16,
    fontSize: fontSizes.xs,
  },
  gridColumn: {
    display: 'flex',
    flexDirection: 'column',
    flexBasis: '25%',
    flexGrow: 0,
    flexShrink: 0,
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    paddingRight: 8,
  },
  innerRow: {
    display: 'flex',
    flexDirection: 'row',
    flexBasis: '25%',
    flexGrow: 0,
  },
  header: {
    fontSize: fontSizes.sm,
    fontWeight: 'bold',
  },
  imageContainer: {
    height: 70,
    objectFit: 'cover',
    paddingTop: 4,
  },
  image: { width: '100%', height: '100%', objectFit: 'cover' },
  addressContainer: {
    backgroundColor: colors.backgroundAccentDark,
    color: colors.background,
  },
  address: {
    padding: 4,
    fontSize: fontSizes.sm,
  },
  label: {
    backgroundColor: colors.surface,
    padding: 4,
    border: `1px solid ${colors.border}`,
    borderTop: 0,
    borderRight: 0,
    flexGrow: 1,
    flexShrink: 1,
  },
  value: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexShrink: 1,
    padding: 4,
    borderBottom: `1px solid ${colors.border}`,
    borderLeft: `1px solid ${colors.border}`,
    minHeight: 18,
  },
  negativeAdjustment: {
    color: colors.negative,
  },
  positiveAdjustment: {
    color: colors.positive,
  },
  text: {
    marginVertical: 'auto',
  },
  borderTop: {
    borderTop: `2px solid ${colors.border}`,
  },
  bold: {
    fontWeight: 'bold',
    fontSize: fontSizes.sm,
  },
});

export const salesComparisonMapPageStyles = StyleSheet.create({
  summaryContainer: {
    display: 'flex',
    flexDirection: 'row',
    paddingHorizontal: 0,
    paddingVertical: 8,
    justifyContent: 'space-between',
    alignItems: 'center',
    alignSelf: 'stretch',
  },
  summaryItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: 4,
    gap: 4,
  },
  summaryIcon: {
    width: 13,
    height: 16,
    objectFit: 'contain',
    padding: 2,
  },
  summaryItemContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  mapContainer: {
    width: 550,
    height: 350,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  },
});

export const depreciatedCostAdjustmentsPageStyles = StyleSheet.create({
  mainDwellingCostBreakdownContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 32,
  },
  roundingAndThresholdsItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  roundingAndThresholdsItemTopRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  additionalStructure: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
});

export const commentarySectionStyles = StyleSheet.create({
  multiCommentarySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  titleContainer: {
    marginBottom: 4,
  },
});

export const priorSalesPageStyles = StyleSheet.create({
  table: {
    display: 'flex',
    flexDirection: 'row',
    gap: 1,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    backgroundColor: colors.strokeLight,
    border: `1px solid ${colors.strokeLight}`,
    flex: '0 0 auto',
    alignSelf: 'flex-start',
  },
  tableColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  tableCell: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    minHeight: 20,
    backgroundColor: colors.background,
  },
});

export const certificationPageStyles = StyleSheet.create({
  text: {
    fontSize: fontSizes.lg,
    color: colors.text,
    fontWeight: 400,
    lineHeight: 1.5,
  },
  title: {
    fontSize: fontSizes.xl,
    color: colors.text,
    paddingTop: 8,
    paddingBottom: 12,
    fontWeight: 'bold',
  },
  signatureSection: {
    marginTop: 8,
  },
  signatureContainer: {
    padding: 15,
    borderWidth: 1,
    borderRadius: 5,
    borderStyle: 'solid',
    borderColor: colors.border,
    width: '100%',
    gap: 12,
  },
  commentarySection: {
    marginTop: 8,
  },
  signatureSectionFieldsContainer: {
    flexDirection: 'row',
  },
  signatureSectionLabel: {
    flex: 1,
    fontSize: fontSizes.lg,
    color: colors.textLight,
  },
  signatureSectionField: {
    flex: 1,
    fontSize: fontSizes.lg,
    color: colors.text,
  },
  signatureSectionImageContainer: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  signatureImage: {
    height: 40,
    width: 150,
    objectFit: 'contain',
  },
  stateSealImage: {
    width: 50,
    height: 50,
    objectFit: 'contain',
  },
  bulletPoint: {
    width: 15,
    marginRight: 10,
    justifyContent: 'flex-end',
    flexDirection: 'row',
  },
  listElement: {
    flexDirection: 'row',
  },
  certificationStatementPageContainer: {
    width: '100%',
    marginTop: 5,
    paddingRight: 30,
  },
  certificationStatementText: {
    fontSize: fontSizes.lg,
    color: colors.text,
    fontWeight: 400,
    lineHeight: 1.15,
  },
  certificationStatementTitleContainer: {
    marginBottom: 5,
  },
});

export const gridStyles = StyleSheet.create({
  gridContainer: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderBottom: 0,
  },
  cardContainer: {
    display: 'flex',
    border: `1px solid ${colors.border}`,
    borderRadius: 4,
  },
  gridRow: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderBottom: `1px solid ${colors.border}`,
  },
  gridCol: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
  },
  gridCell: {
    display: 'flex',
    paddingVertical: 4,
    paddingLeft: 8,
    alignItems: 'flex-start',
    borderRight: `1px solid ${colors.border}`,
    flex: 1,
  },
  listItemDotSmall: {
    height: '3px',
    width: '3px',
    borderRadius: '1.5px',
    backgroundColor: colors.text,
    display: 'flex',
    alignSelf: 'center',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  gridSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  gridSection70: {
    width: '70%',
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  verticalSpace: {
    width: 1,
    height: 8,
  }
});

export const reconciliationPageStyles = StyleSheet.create({
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginTop: 4,
  },
});
